/**
 * Application Testing tags (sample set). Tickets do not store tags yet, so a ticket shows the tags whose words
 * appear in its subject (see `tagsFor`). Once tags are saved on the ticket, `tagsFor` is replaced by the saved list
 * and this catalogue becomes the picker's options.
 */

import { htmlToText } from './applicationTestingText';

export interface TagCategory {
  category: string;
  tags: string[];
}

export const TAG_CATALOGUE: TagCategory[] = [
  {
    category: 'Functional Area',
    tags: [
      'Accounts',
      'Tax',
      'Returns',
      'Trial Balance',
      'Payroll',
      'Company Info',
      'Client Management',
      'Reports',
      'eInvoicing',
    ],
  },
  {
    category: 'Issue Type',
    tags: [
      'Bug',
      'Enhancement',
      'Validation',
      'UI/UX',
      'Requirement Gap',
      'Data Issue',
      'Regression',
      'Change Request',
    ],
  },
  {
    category: 'Technical Area',
    tags: ['API', 'Database', 'Integration', 'Performance', 'Security', 'Authentication', 'Infrastructure', 'Logging'],
  },
  {
    category: 'Feature',
    tags: ['Import', 'Export', 'Reporting', 'Scheduler', 'Notifications', 'Search', 'Bulk Processing', 'Audit Trail'],
  },
  {
    category: 'Dependency / Ownership',
    tags: [
      'BA Action',
      'Developer Action',
      'QA Action',
      'Client Dependency',
      'Third-Party Dependency',
      'Environment Issue',
      'Awaiting Approval',
    ],
  },
];

/** Tags renamed in the catalogue: a ticket that still carries the old name keeps it under the new one. */
const RENAMED_TAGS: Record<string, string> = { UI: 'UI/UX' };

export const ALL_TAGS: string[] = TAG_CATALOGUE.flatMap((c) => c.tags);

type Tone = 'red' | 'blue' | 'purple' | 'green' | 'pink' | 'amber' | 'teal';

/** Each tag keeps one colour everywhere it appears (chips, filter, dashboard bars). */
const TAG_TONE: Record<string, Tone> = {
  Accounts: 'blue',
  Tax: 'pink',
  Returns: 'purple',
  'Trial Balance': 'purple',
  Payroll: 'green',
  Bug: 'red',
  Enhancement: 'purple',
  Validation: 'red',
  'UI/UX': 'blue',
  API: 'teal',
  Database: 'teal',
  Integration: 'red',
  Performance: 'amber',
  Import: 'blue',
  Export: 'green',
  Reporting: 'green',
  Scheduler: 'blue',
  'Company Info': 'blue',
  'Client Management': 'blue',
  Reports: 'green',
  eInvoicing: 'pink',
  'Requirement Gap': 'amber',
  'Data Issue': 'red',
  Regression: 'red',
  'Change Request': 'purple',
  Security: 'red',
  Authentication: 'teal',
  Infrastructure: 'teal',
  Logging: 'teal',
  Notifications: 'amber',
  Search: 'blue',
  'Bulk Processing': 'purple',
  'Audit Trail': 'green',
  'BA Action': 'amber',
  'Developer Action': 'blue',
  'QA Action': 'purple',
  'Client Dependency': 'pink',
  'Third-Party Dependency': 'pink',
  'Environment Issue': 'red',
  'Awaiting Approval': 'amber',
};

const TONES: Record<Tone, { background: string; color: string; bar: string }> = {
  red: { background: '#FDE8EA', color: '#C8102E', bar: '#F4A3B0' },
  blue: { background: '#E6F1FD', color: '#1D6FD8', bar: '#9CC9F5' },
  purple: { background: '#F1E8FD', color: '#7C3AED', bar: '#CDB5F7' },
  green: { background: '#E3F6EA', color: '#15803D', bar: '#93D6AC' },
  pink: { background: '#FCE7F3', color: '#BE185D', bar: '#F5A9CF' },
  amber: { background: '#FEF3C7', color: '#B45309', bar: '#F7CF7A' },
  teal: { background: '#E0F5F5', color: '#0F766E', bar: '#8FD3CF' },
};

export function tagTone(tag: string) {
  return TONES[TAG_TONE[tag] ?? 'blue'];
}

/** Words that point at a tag beyond its own name (matched case-insensitively, on word starts). */
const KEYWORDS: Record<string, string[]> = {
  Accounts: ['accounts', 'account code', 'annual accounts'],
  Tax: ['tax', 'ct600', 'vat'],
  Returns: ['return', 'filing'],
  'Trial Balance': ['trial balance', 'tb'],
  Payroll: ['payroll'],
  Bug: ['error', 'not working', 'not coming', 'wrong', 'unable', 'fails', 'broken', 'crash'],
  Enhancement: ['should', 'allow', 'enhance', 'improve', 'remove', 'add '],
  Validation: ['validation', 'validate'],
  'UI/UX': ['ui', 'colour', 'color', 'highlight', 'screen', 'page', 'button', 'dropdown', 'display', 'layout'],
  API: ['api'],
  Database: ['database'],
  Integration: ['integration', 'companies house', 'hmrc'],
  Performance: ['slow', 'performance', 'takes'],
  Import: ['import'],
  Export: ['export', 'download'],
  Reporting: ['report', 'ixbrl', 'note'],
  Scheduler: ['schedul', 'periodic'],
};

// ---- Tags chosen by hand ----
// Until tickets store their tags on the server, a ticket's chosen tags are kept in this browser (per ticket id).
// A ticket nobody has tagged by hand shows the sample tags its words suggest.

const STORE_KEY = 'application-testing-ticket-tags';
const listeners = new Set<() => void>();
let version = 0;

function readStore(): Record<string, string[]> {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, string[]>) : {};
  } catch {
    return {};
  }
}

let store: Record<string, string[]> = readStore();

/** The tags chosen by hand for a ticket, or null when nobody has chosen any. */
export function chosenTags(ticketId: string): string[] | null {
  const tags = store[ticketId];
  return Array.isArray(tags)
    ? [...new Set(tags.map((tag) => RENAMED_TAGS[tag] ?? tag))].filter((tag) => ALL_TAGS.includes(tag))
    : null;
}

/** Saves a ticket's chosen tags (in catalogue order) and tells every screen showing it. */
export function setChosenTags(ticketId: string, tags: string[]): void {
  store = { ...store, [ticketId]: ALL_TAGS.filter((tag) => tags.includes(tag)) };
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable (private window): the choice still applies until the page is reloaded.
  }
  version++;
  listeners.forEach((listener) => listener());
}

// Tags saved on the server. `seedServerTags` loads them into the same store the screens read, and remembers what the
// server holds so a change is only sent when it differs.
const serverKeys = new Map<string, string>();
const keyOf = (tags: string[]) => ALL_TAGS.filter((tag) => tags.includes(tag)).join('|');

/** Loads the tags the server holds for these tickets (rows with `tags: null` have none chosen and are left alone). */
export function seedServerTags(tickets: { id: string; tags?: string[] | null }[]): void {
  let changed = false;
  for (const ticket of tickets) {
    if (!Array.isArray(ticket.tags)) continue;
    const mapped = [...new Set(ticket.tags.map((tag) => RENAMED_TAGS[tag] ?? tag))].filter((tag) =>
      ALL_TAGS.includes(tag),
    );
    serverKeys.set(ticket.id, keyOf(mapped));
    if (keyOf(chosenTags(ticket.id) ?? []) !== keyOf(mapped) || chosenTags(ticket.id) === null) {
      store = { ...store, [ticket.id]: ALL_TAGS.filter((tag) => mapped.includes(tag)) };
      changed = true;
    }
  }
  if (changed) {
    version++;
    listeners.forEach((listener) => listener());
  }
}

/** True when the tags shown for a ticket differ from what the server last held (so they need saving). */
export function tagsNeedSaving(ticketId: string, tags: string[]): boolean {
  return (serverKeys.get(ticketId) ?? keyOf([])) !== keyOf(tags) && (serverKeys.has(ticketId) || tags.length > 0);
}

/** The tags the server last held for a ticket (none when it has never been told). */
export function tagsHeldFor(ticketId: string): string[] {
  const key = serverKeys.get(ticketId);
  return key ? key.split('|') : [];
}

/** Records that the server now holds these tags for the ticket. */
export function markTagsSaved(ticketId: string, tags: string[]): void {
  serverKeys.set(ticketId, keyOf(tags));
}

/** For useSyncExternalStore: re-render whenever any ticket's tags change. */
export function subscribeToTags(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const tagsVersion = () => version;

/** Test helper: forgets every chosen tag. */
export function resetChosenTags(): void {
  store = {};
  serverKeys.clear();
  try {
    window.localStorage.removeItem(STORE_KEY);
  } catch {
    // ignore
  }
  version++;
  listeners.forEach((listener) => listener());
}

/** A ticket's tags: the ones chosen by hand, else the sample tags its words suggest. */
export function ticketTags(ticket: { id: string; subject?: string | null; description?: string | null }): string[] {
  return chosenTags(ticket.id) ?? tagsFor(ticket);
}

const MAX_TAGS = 3;

const startsWord = (text: string, word: string) =>
  new RegExp(`(^|[^a-z0-9])${word.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(text);

/** The sample tags for a ticket: Issue Type first, then the area and feature tags, at most three. */
export function tagsFor(ticket: { subject?: string | null; description?: string | null }): string[] {
  const text = `${ticket.subject ?? ''} ${htmlToText(ticket.description)}`;
  const order = ['Issue Type', 'Functional Area', 'Feature', 'Technical Area'];
  const found: string[] = [];
  for (const category of order) {
    for (const tag of TAG_CATALOGUE.find((c) => c.category === category)?.tags ?? []) {
      if (found.length >= MAX_TAGS) return found;
      if ([tag, ...(KEYWORDS[tag] ?? [])].some((word) => startsWord(text, word.toLowerCase()))) found.push(tag);
    }
  }
  return found;
}
